import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSettings } from '@/features/config/hooks/use-config';
import { Button } from '@/shared/ui/Button';
import { TextArea } from '@/shared/ui/TextArea';
import { TextField } from '@/shared/ui/TextField';

/**
 * Default freelance service-radius in km (Phase 6, P6-6). Named constant per
 * D-014 rather than a magic number — brief called for 10km default. The DB
 * check constraint stays at 500km (relaxing later is safer than tightening).
 */
export const DEFAULT_SERVICE_RADIUS_KM = 10;

export interface ProfileFormValues {
  fullName: string;
  city: string | null;
  bio: string | null;
  yearsExperience: number | null;
  qualifications: string | null;
  serviceLat: number | null;
  serviceLng: number | null;
  serviceRadiusKm: number;
  canKhutbah: boolean;
  // Phase 6 (D-014): multi-role flags.
  isImam: boolean;
  isMuezzin: boolean;
  isAppointed: boolean;
  isFreelance: boolean;
}

interface ProfileFormProps {
  initial?: Partial<ProfileFormValues>;
  submitLabel: string;
  submitting: boolean;
  serverError?: string | null;
  onSubmit: (values: ProfileFormValues) => void;
}

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

/** Reusable profile form used by onboarding and the edit-profile route (D-014). */
export function ProfileForm({
  initial,
  submitLabel,
  submitting,
  serverError,
  onSubmit,
}: ProfileFormProps): JSX.Element {
  const { t } = useTranslation();
  const [isImam, setIsImam] = useState(initial?.isImam ?? false);
  const [isMuezzin, setIsMuezzin] = useState(initial?.isMuezzin ?? false);
  const [isAppointed, setIsAppointed] = useState(initial?.isAppointed ?? false);
  const [isFreelance, setIsFreelance] = useState(initial?.isFreelance ?? false);
  const [fullName, setFullName] = useState(initial?.fullName ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [bio, setBio] = useState(initial?.bio ?? '');
  const [years, setYears] = useState(
    initial?.yearsExperience != null ? String(initial.yearsExperience) : '',
  );
  const [qualifications, setQualifications] = useState(initial?.qualifications ?? '');
  const [lat, setLat] = useState(initial?.serviceLat != null ? String(initial.serviceLat) : '');
  const [lng, setLng] = useState(initial?.serviceLng != null ? String(initial.serviceLng) : '');
  const [radius, setRadius] = useState(
    String(initial?.serviceRadiusKm ?? DEFAULT_SERVICE_RADIUS_KM),
  );
  const [canKhutbah, setCanKhutbah] = useState(initial?.canKhutbah ?? false);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    role?: string;
    fullName?: string;
    years?: string;
    coords?: string;
    radius?: string;
  }>({});

  const provider = isImam || isMuezzin;
  const { flag } = useAppSettings();
  const khutbahMatchingEnabled = flag('feature.khutbah_matching');

  function locateMe(): void {
    if (!navigator.geolocation) {
      setGeoError(t('profile.locationError'));
      return;
    }
    setGeoError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(5));
        setLng(pos.coords.longitude.toFixed(5));
        setLocating(false);
      },
      () => {
        setGeoError(t('profile.locationError'));
        setLocating(false);
      },
    );
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    const nextErrors: typeof errors = {};

    // At least one role flag must be set — otherwise the profile can't be
    // categorized on the marketplace.
    if (!isImam && !isMuezzin && !isAppointed && !isFreelance) {
      nextErrors.role = t('profile.roleRequired');
    }
    if (fullName.trim().length < 2) nextErrors.fullName = t('profile.fullNameInvalid');

    let yearsValue: number | null = null;
    let latValue: number | null = null;
    let lngValue: number | null = null;
    let radiusValue = DEFAULT_SERVICE_RADIUS_KM;

    if (provider) {
      if (years.trim().length > 0) {
        const parsed = Number(years);
        if (!Number.isInteger(parsed) || parsed < 0 || parsed > 80) {
          nextErrors.years = t('profile.yearsInvalid');
        } else {
          yearsValue = parsed;
        }
      }

      const latStr = lat.trim();
      const lngStr = lng.trim();
      if (latStr !== '' || lngStr !== '') {
        const la = Number(latStr);
        const ln = Number(lngStr);
        if (
          latStr === '' ||
          lngStr === '' ||
          Number.isNaN(la) ||
          Number.isNaN(ln) ||
          la < -90 ||
          la > 90 ||
          ln < -180 ||
          ln > 180
        ) {
          nextErrors.coords = t('profile.coordinatesInvalid');
        } else {
          latValue = la;
          lngValue = ln;
        }
      }

      const r = Number(radius.trim() === '' ? String(DEFAULT_SERVICE_RADIUS_KM) : radius);
      if (!Number.isInteger(r) || r < 1 || r > 500) {
        nextErrors.radius = t('profile.radiusInvalid');
      } else {
        radiusValue = r;
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit({
      fullName: fullName.trim(),
      city: emptyToNull(city),
      bio: emptyToNull(bio),
      yearsExperience: yearsValue,
      qualifications: provider ? emptyToNull(qualifications) : null,
      serviceLat: latValue,
      serviceLng: lngValue,
      serviceRadiusKm: radiusValue,
      canKhutbah: isImam && khutbahMatchingEnabled ? canKhutbah : false,
      isImam,
      isMuezzin,
      isAppointed,
      isFreelance,
    });
  }

  return (
    <form className="stack" onSubmit={handleSubmit} noValidate>
      <p className="field__label">{t('profile.rolesLabel')}</p>
      <p className="muted">{t('profile.rolesHint')}</p>
      <div className="role-grid">
        <RoleCheckbox
          label={t('profile.role_isImam')}
          description={t('profile.role_isImamHint')}
          checked={isImam}
          onChange={setIsImam}
        />
        <RoleCheckbox
          label={t('profile.role_isMuezzin')}
          description={t('profile.role_isMuezzinHint')}
          checked={isMuezzin}
          onChange={setIsMuezzin}
        />
        <RoleCheckbox
          label={t('profile.role_isAppointed')}
          description={t('profile.role_isAppointedHint')}
          checked={isAppointed}
          onChange={setIsAppointed}
        />
        <RoleCheckbox
          label={t('profile.role_isFreelance')}
          description={t('profile.role_isFreelanceHint')}
          checked={isFreelance}
          onChange={setIsFreelance}
        />
      </div>
      {errors.role ? (
        <p className="text-danger" role="alert">
          {errors.role}
        </p>
      ) : null}

      <TextField
        label={t('profile.fullName')}
        placeholder={t('profile.fullNameHint')}
        value={fullName}
        error={errors.fullName}
        onChange={(e) => setFullName(e.target.value)}
        autoComplete="name"
      />
      <TextField
        label={t('profile.city')}
        placeholder={t('profile.cityHint')}
        value={city}
        onChange={(e) => setCity(e.target.value)}
      />
      <TextArea
        label={t('profile.bio')}
        placeholder={t('profile.bioHint')}
        value={bio}
        maxLength={1000}
        onChange={(e) => setBio(e.target.value)}
      />
      {provider ? (
        <>
          <TextField
            label={t('profile.yearsExperience')}
            placeholder={t('profile.yearsHint')}
            value={years}
            error={errors.years}
            inputMode="numeric"
            ltr
            onChange={(e) => setYears(e.target.value.replace(/[^0-9]/g, ''))}
          />
          <TextArea
            label={t('profile.qualifications')}
            placeholder={t('profile.qualificationsHint')}
            value={qualifications}
            maxLength={2000}
            onChange={(e) => setQualifications(e.target.value)}
          />
          {isImam && khutbahMatchingEnabled ? (
            <label className="row" style={{ gap: 'var(--space-2)' }}>
              <input
                type="checkbox"
                checked={canKhutbah}
                onChange={(e) => setCanKhutbah(e.target.checked)}
              />
              <span>{t('profile.canKhutbah')}</span>
            </label>
          ) : null}

          <p className="field__label">{t('profile.serviceArea')}</p>
          <p className="muted">{t('profile.serviceAreaHint')}</p>
          <Button variant="outline" loading={locating} onClick={locateMe}>
            {t('profile.locateMe')}
          </Button>
          <div className="row">
            <TextField
              label={t('profile.latitude')}
              value={lat}
              ltr
              inputMode="decimal"
              onChange={(e) => setLat(e.target.value.replace(/[^0-9.-]/g, ''))}
            />
            <TextField
              label={t('profile.longitude')}
              value={lng}
              ltr
              inputMode="decimal"
              onChange={(e) => setLng(e.target.value.replace(/[^0-9.-]/g, ''))}
            />
          </div>
          {errors.coords ? (
            <p className="text-danger" role="alert">
              {errors.coords}
            </p>
          ) : null}
          {geoError ? (
            <p className="text-danger" role="alert">
              {geoError}
            </p>
          ) : null}
          <TextField
            label={t('profile.serviceRadiusKm')}
            value={radius}
            error={errors.radius}
            inputMode="numeric"
            ltr
            onChange={(e) => setRadius(e.target.value.replace(/[^0-9]/g, ''))}
          />
        </>
      ) : null}

      {serverError ? (
        <p className="text-danger" role="alert">
          {serverError}
        </p>
      ) : null}

      <Button type="submit" block loading={submitting}>
        {submitLabel}
      </Button>
    </form>
  );
}

interface RoleCheckboxProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function RoleCheckbox({ label, description, checked, onChange }: RoleCheckboxProps): JSX.Element {
  return (
    <label className={`role-card${checked ? ' role-card--selected' : ''}`} aria-label={label}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ margin: 0 }}
      />
      <div>
        <strong>{label}</strong>
        <p className="muted">{description}</p>
      </div>
    </label>
  );
}
